import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/icon';
import api from '../../../utils/axiosConfig';
import {
  EmptyState, FilterPanel, formatDateTime, ProgressPaginationControl,
  SummaryCards, WorkOrderCard, WorkOrderSkeletons
} from './components';
import { ProgressFilters, ProgressResponse, StatusOption } from './types';
import './progress-work-order.scss';

const pad = (value: number) => String(value).padStart(2, '0');
const dateValue = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const defaultFilters = (): ProgressFilters => {
  const now = new Date();
  return {
    awal: dateValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    akhir: dateValue(now),
    search: '',
    status: 'semua',
    sort_by: 'tgl',
    sort_direction: 'desc',
    page: 1,
    per_page: 20,
    include_timeline: 1,
    activity_limit: 5,
  };
};

const statusKeys = ['semua', 'draft', 'siap', 'proses', 'selesai', 'hold', 'terlambat'];
const sortKeys = ['tgl', 'deadline', 'kode', 'product', 'progress', 'aktivitas_terakhir'];
const fallbackStatuses: StatusOption[] = [
  { key: 'semua', label: 'Semua' }, { key: 'draft', label: 'Draft' },
  { key: 'siap', label: 'Siap diproses' }, { key: 'proses', label: 'Sedang diproses' },
  { key: 'selesai', label: 'Selesai' }, { key: 'hold', label: 'Ditahan' },
  { key: 'terlambat', label: 'Terlambat' },
];

const parseNumber = (value: string | null, fallback: number, min: number, max: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= min && parsed <= max ? parsed : fallback;
};

const parseFilters = (params: URLSearchParams): ProgressFilters => {
  const defaults = defaultFilters();
  const status = params.get('status');
  const sortBy = params.get('sort_by');
  const direction = params.get('sort_direction');
  const perPage = parseNumber(params.get('per_page'), defaults.per_page, 1, 50);
  return {
    ...defaults,
    awal: params.get('awal') || defaults.awal,
    akhir: params.get('akhir') || defaults.akhir,
    search: params.get('search') || '',
    status: (statusKeys.includes(status || '') ? status : defaults.status) as ProgressFilters['status'],
    sort_by: (sortKeys.includes(sortBy || '') ? sortBy : defaults.sort_by) as ProgressFilters['sort_by'],
    sort_direction: direction === 'asc' || direction === 'desc' ? direction : defaults.sort_direction,
    page: parseNumber(params.get('page'), 1, 1, Number.MAX_SAFE_INTEGER),
    per_page: [10, 20, 30, 50].includes(perPage) ? perPage : defaults.per_page,
    include_timeline: params.get('include_timeline') === '0' ? 0 : 1,
    activity_limit: parseNumber(params.get('activity_limit'), defaults.activity_limit, 0, 20),
  };
};

const serializeFilters = (filters: ProgressFilters) => {
  const params = new URLSearchParams();
  params.set('awal', filters.awal);
  params.set('akhir', filters.akhir);
  if (filters.search.trim()) params.set('search', filters.search.trim());
  params.set('status', filters.status);
  params.set('sort_by', filters.sort_by);
  params.set('sort_direction', filters.sort_direction);
  params.set('page', String(filters.page));
  params.set('per_page', String(filters.per_page));
  params.set('include_timeline', String(filters.include_timeline));
  params.set('activity_limit', String(filters.activity_limit));
  return params;
};

const getErrorMessage = (error: any) => {
  const errors = error?.response?.data?.errors;
  const validationMessage = errors && Object.values(errors).flat()[0];
  return String(validationMessage || error?.response?.data?.message || error?.message || 'Progress Work Order gagal dimuat.');
};

export default function KddProgressWorkOrderIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const filters = useMemo(() => parseFilters(new URLSearchParams(queryString)), [queryString]);
  const [draftFilters, setDraftFilters] = useState<ProgressFilters>(filters);
  const [report, setReport] = useState<ProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterError, setFilterError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const hasRequiredQuery = searchParams.has('awal') && searchParams.has('akhir');

  useEffect(() => {
    if (!hasRequiredQuery) setSearchParams(serializeFilters(filters), { replace: true });
  }, [filters, hasRequiredQuery, setSearchParams]);

  useEffect(() => setDraftFilters(filters), [filters]);

  useEffect(() => {
    if (!hasRequiredQuery) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setReport(null);
    api.get('/production/work-order-kedinding/progress', { params: filters, signal: controller.signal })
      .then(({ data }) => {
        if (data?.success === false) throw new Error(data.message || 'Progress Work Order gagal dimuat.');
        setReport(data as ProgressResponse);
      })
      .catch(requestError => {
        if (requestError?.code !== 'ERR_CANCELED' && requestError?.name !== 'CanceledError') setError(getErrorMessage(requestError));
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters, hasRequiredQuery, refreshKey]);

  const applyFilters = () => {
    setFilterError('');
    if (!draftFilters.awal || !draftFilters.akhir) return setFilterError('Tanggal awal dan tanggal akhir wajib diisi.');
    if (draftFilters.awal > draftFilters.akhir) return setFilterError('Tanggal awal tidak boleh melebihi tanggal akhir.');
    setSearchParams(serializeFilters({ ...draftFilters, search: draftFilters.search.trim(), page: 1 }));
  };

  const resetFilters = () => {
    const defaults = defaultFilters();
    setDraftFilters(defaults);
    setFilterError('');
    setSearchParams(serializeFilters(defaults));
  };

  const changePage = (page: number) => {
    setSearchParams(serializeFilters({ ...filters, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const refresh = () => setRefreshKey(current => current + 1);
  const statusOptions = report?.meta.status_options?.length ? report.meta.status_options : fallbackStatuses;

  return <div className="kdd-progress-page">
    <Box className="progress-page-heading">
      <Box><Typography variant="h4" fontWeight={850}>Progress Work Order Kedinding</Typography><Typography color="text.secondary">Monitoring timeline dan hasil setiap tahapan produksi WO Kedinding.</Typography></Box>
      <Stack className="heading-actions" direction="row" spacing={1.2} alignItems="center">
        {report?.meta.generated_at && <Chip variant="outlined" icon={<Icon icon="mdi:clock-outline" />} label={`Diperbarui ${formatDateTime(report.meta.generated_at, true)}`} />}
        <Button variant="contained" onClick={refresh} disabled={loading} startIcon={<Icon icon="mdi:refresh" />}>Refresh</Button>
      </Stack>
    </Box>

    <FilterPanel value={draftFilters} statusOptions={statusOptions} loading={loading} onChange={setDraftFilters} onApply={applyFilters} onReset={resetFilters} />
    {filterError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFilterError('')}>{filterError}</Alert>}
    {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={refresh}>Coba lagi</Button>}>{error}</Alert>}

    <SummaryCards summary={report?.summary} loading={loading} />

    <Box className="progress-list-heading"><Box><Typography variant="h6" fontWeight={850}>Daftar Work Order</Typography><Typography variant="body2" color="text.secondary">{report?.meta.progress_calculation || 'Progress dihitung dari seluruh tahap produksi.'}</Typography></Box>{report && <Chip color="primary" variant="outlined" label={`${report.pagination.total} WO`} />}</Box>

    {loading ? <WorkOrderSkeletons /> : report?.data?.length ? <Stack spacing={2}>{report.data.map(workOrder => <WorkOrderCard key={workOrder.wo_order_id} workOrder={workOrder} />)}</Stack> : !error ? <EmptyState /> : null}
    {report && <ProgressPaginationControl pagination={report.pagination} onChange={changePage} />}
  </div>;
}
